import { MetroEvent } from '../MetroEvent';
import { MetroUser } from '../MetroUser';
import {
  validateDevice,
  invalidDeviceObjectError,
  invalidOSObjectError,
  invalidPlatformObjectError,
} from './validators';
import type { PlatformOrOS, Device, Web, MetroHelperEvent } from './types';
import { Environment } from '@/constants';
import { getEnvironmentConstants } from '@/functions';

export const invalidMetroUserError = '`metroUser` argument must be a valid MetroUser object';
export const invalidEnvironmentError = '`environment` argument must be a valid string';
export const invalidAppNameError = '`appName` argument must be a valid string';
export const invalidSessionIdError = '`sessionId` argument must be a valid string';
export const invalidEventsArrayError = '`events` argument must be an array with non-zero length';
export const invalidEventObjectError =
  'each item in `events` argument must be a string or a MetroEvent object';
export { invalidDeviceObjectError, invalidOSObjectError, invalidPlatformObjectError };

const defaultOS: PlatformOrOS = {
  name: undefined,
  architecture: undefined,
  version: undefined,
};

const defaultPlatform: PlatformOrOS = {
  name: undefined,
  architecture: undefined,
  version: undefined,
};

export const defaultDevice: Device = {
  model: undefined,
  os: defaultOS,
  platform: defaultPlatform,
};

/**
 * @name MetroHelper
 * @description The `MetroHelper` class allows for easy metric sends to the Metro
 * service in JS environments. In addition to the MetroHelper class itself, there
 * are two utility classes (`MetroUser` and `MetroEvent`) that simplify the
 * construction of a valid `MetroHelper`.
 *
 * @example
 * const metroUser = new MetroUser('my-client-id');
 * const metroHelper = new MetroHelper('my-app-name', 'PROD', metroUser, 'optional-session-id');
 *
 * const metroEvent = new MetroEvent('my-event-name', 'optional-unit', 'optional-value');
 *
 * const response = metroHelper.sendMetrics([metroEvent]);
 *
 * ## Error Handling
 *
 * The `MetroHelper`, `MetroUser`, and `MetroEvent` classes have thorough error handling and
 * validation and will let the consumer know when things go wrong. It is recommended to use
 * the `MetroUser` and `MetroEvent` classes rather than create JS objects from scratch to
 * avoid unclear errors.
 */
export class MetroHelper {
  readonly sessionId: string;

  readonly user: MetroUser | null;

  app: { name: string; environment: Uppercase<Environment> };

  web: Web;

  device: Device;

  /**
   * @param appName - the name of the app for which
   * metrics will be sent (e.g. 'Zendesk', 'Webapp')
   * @param environment - the current environment (must be one of: 'LOCAL', 'DEV', 'PROD')
   * @param metroUser - the user for which the metrics
   *  apply (optional, must be instance of `MetroUser`)
   * @param sessionId - a unique string that corresponds
   * to the user's current session (optional)
   * @param device - the details of the device, os, and platform (optional)
   */
  constructor(
    appName: string,
    environment: Uppercase<Environment>,
    metroUser: MetroUser | null = null,
    sessionId = '',
    device = defaultDevice,
  ) {
    if (metroUser) {
      MetroUser.validate(metroUser);
    }

    if (typeof environment !== 'string') {
      throw new Error(invalidEnvironmentError);
    }

    if (typeof appName !== 'string') {
      throw new Error(invalidAppNameError);
    }

    if (sessionId && typeof sessionId !== 'string') {
      throw new Error(invalidSessionIdError);
    }

    validateDevice(device);

    this.sessionId = sessionId;
    this.user = metroUser;
    this.web = MetroHelper.createWebObject();
    this.app = {
      name: appName,
      environment,
    };
    this.device = device;
  }

  static createWebObject() {
    return {
      userAgent: window.navigator.userAgent,
      hostName: window.location.hostname,
      pageName: window.document.title,
      urlPath: window.location.pathname,
      urlQuery: window.location.search,
      referrer: window.document.referrer,
    };
  }

  static validateEvents(events: MetroHelperEvent | MetroHelperEvent[]) {
    if (!(Array.isArray(events) && events.length)) {
      throw new Error(invalidEventsArrayError);
    }

    events.forEach((event) => {
      if (typeof event !== 'string' && typeof event !== 'object') {
        throw new Error(invalidEventObjectError);
      }

      if (typeof event === 'object') {
        MetroEvent.isValid(event);
      }
    });

    return true;
  }

  createPayload(events: MetroHelperEvent[] | MetroHelperEvent) {
    return {
      sessionId: this.sessionId,
      events,
      user: this.user,
      web: this.web,
      app: this.app,
      device: this.device,
    };
  }

  /**
   * @param events - an array of `MetroEvent` objects or event names (as strings)
   *
   * NOTE: using `MetroHelper`, it should be _very_ difficult (hopefully impossible)
   * to get this 500 error.
   */
  sendMetrics = async (events: MetroEvent[] | string[]) => {
    try {
      MetroHelper.validateEvents(events);
    } catch (error) {
      return Promise.reject(error instanceof Error ? error.message : error);
    }

    const mappedEvents = events.map((item) => {
      if (typeof item === 'string') {
        return new MetroEvent(item);
      }

      return item;
    });

    const payload = this.createPayload(mappedEvents);

    const envConstants = getEnvironmentConstants(
      this.app.environment.toLowerCase() as Lowercase<Environment>,
    );
    const { metro } = envConstants;
    const { baseUrl } = metro;

    const response = await fetch(`${baseUrl}/v2/metric`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const body = await response.text();

    return {
      status: response.status,
      body,
    };
  };

  /**
   * @param events - an array of `MetroEvent` objects or event names (as strings)
   *
   * @description
   * Send a metric with the browsers sendBeacon API rather than fetch. This will not return
   * an error from metro itself, but just if the browser was able to queue the message.
   */
  sendMetricsWithBeacon = async (
    events: MetroHelperEvent[],
  ): Promise<{ status: 200; body: 'success' } | { status: 500; body: 'failure' }> => {
    try {
      MetroHelper.validateEvents(events);
    } catch (error) {
      return Promise.reject(error instanceof Error ? error.message : error);
    }

    const mappedEvents = events.map((item) => {
      if (typeof item === 'string') {
        return new MetroEvent(item);
      }

      return item;
    });

    const payload = this.createPayload(mappedEvents);

    const baseUrl =
      this.app.environment === 'PROD'
        ? 'https://metro.bombbomb.io'
        : 'https://development-test-metro.dev.bombbomb.io';

    if (
      navigator.sendBeacon(
        `${baseUrl}/v2/metric`,
        new Blob([JSON.stringify(payload, null, 0)], { type: 'application/json' }),
      )
    ) {
      return {
        status: 200,
        body: 'success',
      };
    }

    return {
      status: 500,
      body: 'failure',
    };
  };
}
